// ---- The player rig ----
// The hero model: the shared HUMANOID archetype (content/models/player.json —
// the same fully-jointed body humanoid NPCs use), built through the same doc
// interpreter (src/model/index.ts) with the
// character's chosen Appearance overriding the doc's palette keys + hair
// style. The build publishes the SHARED rig contract (src/model/rig.ts) —
// every joint group tagged `userData.limb`, the fist sockets
// `userData.socket` — so the shared gait (src/model/gait.ts, driven with the
// player's own amplitude profile) and the shared weapon-pose / clip system
// (src/anim/pose.ts) animate it exactly like any humanoid NPC — the player is
// just the first rig instance. The exported surface (buildPlayer /
// updatePlayer / setWeapon / startAttack) is unchanged, so combat.ts /
// net.ts / game.ts / journal.ts / the studio are untouched.

import * as THREE from "three";
import type { ItemSpec, WeaponSpec } from "./items/define.ts";
import { itemSpecOrNull } from "./items/index.ts";
import { DEFAULT_APPEARANCE, WEAR_SLOTS, sanitizeAppearance, type Appearance, type BodyBuild, type Gear } from "./shared.ts";
import { buildFromDoc, headwearHidesHair, partGroup, resolvedModelDoc } from "./model/index.ts";
import { deepMerge } from "./model/variants.ts";
import { WEAR_MODEL_SLOTS, type ModelDoc } from "./model/parts.ts";
import { npcMeshGroup } from "./npc/define.ts";
import { disposeGroup } from "./meshkit.ts";
import { collectRig, type RigHandle } from "./model/rig.ts";
import { animateGait, gaitSnapshot, invalidateGaitLimbs, PLAYER_PROFILE } from "./model/gait.ts";
import {
  applyWeaponPose, equipRig, equipOffhand, equipTool, applyBlockPose, applyFishingPose, startRigAttack, type AttackSel,
} from "./anim/pose.ts";
import { UNARMED } from "./anim/clips.ts";
import { applyBodyPose, type PoseRootOut } from "./anim/body-pose.ts";
import { codedWearDef, updateCloakHem } from "./wear/index.ts";

export type { AttackSel };

/** The player's collected rig — same sparse handle shape as every humanoid
 *  (src/model/rig.ts), but the player publishes the FULL joint + socket set. */
export type PlayerParts = RigHandle;

const hex = (n: number) => "#" + ((n >>> 0) & 0xffffff).toString(16).padStart(6, "0");

/** Fold a worn-gear record into the (already appearance-merged) player doc:
 *  cloth recolors palette keys, `body` overrides deep-merge (short sleeves /
 *  shorts), `part` geometry fills the slot's anchor slot(s). Headwear hides
 *  hair when the parts doc says so (default true — helmets/caps; crowns set
 *  `hidesHair: false`). Coded wear (src/wear) attaches AFTER the prim build —
 *  see attachCodedWearOnPlayer. Unknown or mismatched types are skipped. */
function mergeWear(doc: ModelDoc, gear: Gear): void {
  let headPartId: string | undefined;
  // Explicit hidesHair on non-head wear (hooded cloaks on the back slot) also
  // strips hair so a hood shell doesn't clip through a full hair mass.
  let hideHair = false;
  for (const slot of WEAR_SLOTS) {
    const type = gear[slot];
    if (!type) continue;
    const w = itemSpecOrNull(type)?.wear;
    if (!w || w.slot !== slot) continue;
    if (w.recolor) doc.palette = { ...doc.palette, ...w.recolor };
    if (w.body) doc.body = deepMerge(doc.body ?? {}, w.body);
    if (w.part) {
      doc.slots = { ...doc.slots };
      for (const ms of WEAR_MODEL_SLOTS[slot]) doc.slots[ms] = w.part;
      if (slot === "head") headPartId = w.part;
      else if (partGroup(w.part)?.hidesHair === true) hideHair = true;
    }
    if (w.coded && codedWearDef(w.coded)?.hidesHair) hideHair = true;
  }
  // Head slot: omit/true hides; crowns set hidesHair:false. Cloth-only head
  // (no part) still hides — clipping-safe.
  if (gear.head && headwearHidesHair(headPartId ? partGroup(headPartId) : null)) {
    hideHair = true;
  }
  if (hideHair) {
    doc.body = deepMerge(doc.body ?? {}, { head: { hairStyle: "none" } });
  }
}

/** Parent freeform coded wear (cloaks, …) onto the built rig limbs. */
function attachCodedWearOnPlayer(player: THREE.Group, gear: Gear): void {
  for (const slot of WEAR_SLOTS) {
    const type = gear[slot];
    if (!type) continue;
    const w = itemSpecOrNull(type)?.wear;
    if (!w?.coded || w.slot !== slot) continue;
    codedWearDef(w.coded)?.attach(player);
  }
}

/** Build the hero from the shared player model doc, the given look overriding
 *  the doc's palette (shirt/skin/hair/eye) + hair style, and any worn gear
 *  merged on top (recolors + attached parts — see mergeWear). A bare number is
 *  shorthand for "just the shirt" (back-compat with the old signature).
 *  `bodyOverride` is the check/test harness's stout-body path (wear-check
 *  matrix, seat tests) — Appearance carries no build, and game code never
 *  passes it. Applied before mergeWear so gear body merges behave identically. */
export function buildPlayer(
  look: number | Partial<Appearance> = {},
  gear: Gear = {},
  bodyOverride?: { build?: BodyBuild },
): THREE.Group {
  // sanitizeAppearance enforces sex-gated hair/beard (female: no beard; male/female hair sets).
  const a: Appearance = typeof look === "number"
    ? { ...DEFAULT_APPEARANCE, shirt: look }
    : sanitizeAppearance({ ...DEFAULT_APPEARANCE, ...look });

  const doc = structuredClone(resolvedModelDoc("player"));
  if (!doc) throw new Error("content/models/player.json missing from the manifest");
  doc.palette = {
    ...doc.palette,
    shirt: hex(a.shirt), trousers: hex(a.pants), skin: hex(a.skin), hair: hex(a.hair), eye: hex(a.eye),
  };
  const body = (doc.body ?? {}) as Record<string, unknown>;
  body.head = {
    ...(body.head as Record<string, unknown> | undefined),
    hairStyle: a.hairStyle,
    beardStyle: a.beardStyle,
  };
  body.sex = a.sex; // the chargen body-type choice — hydrated into the humanoid frame
  if (bodyOverride?.build) body.build = bodyOverride.build;
  doc.body = body;
  mergeWear(doc, gear); // gear wins over the base look (a tunic replaces your shirt)

  // Same group assembly (shadow flags + prim seed) every NPC build uses.
  const player = npcMeshGroup("player", (p) => buildFromDoc(p, doc));
  // Coded wear (open cloaks, …) parents custom meshes onto torso/head after
  // the prim merge — freeform geometry the parts language can't express.
  attachCodedWearOnPlayer(player, gear);
  // Remember which children came from the build, so redressPlayer can swap the
  // body without touching later additions (name labels on remotes).
  player.userData.buildKids = [...player.children];

  player.userData.parts = collectRig(player);
  player.userData.gaitProfile = PLAYER_PROFILE; // the hero's historical gait feel
  player.userData.attackT = 0;          // seconds left in the current attack swing
  player.userData.attackStyle = "primary" as AttackSel; // which swing is playing
  player.userData.weaponSpec = null as WeaponSpec | null; // equipped weapon's combat data
  player.userData.weaponMesh = null as THREE.Group | null; // the held mesh (in handMain)
  player.userData.combatReady = false;  // hold a ready stance (set by combat.ts)

  return player;
}

/** Equip (or clear, with spec=null) a weapon: parents its model into the main
 *  hand with the spec's hold transform. Reused for the local player AND remotes. */
export function setWeapon(player: THREE.Group, spec: ItemSpec | null): void {
  equipRig(player, player.userData.parts as RigHandle, spec);
}

/** Equip (or clear) an offhand shield (plan 14) — same pattern as setWeapon,
 *  seated into the handOff socket. Local player, remotes, and the studio. */
export function setShield(player: THREE.Group, spec: ItemSpec | null): void {
  equipOffhand(player, player.userData.parts as RigHandle, spec);
}

/** Equip (or clear) a held TOOL — a fishing rod (plan 10) — into the main-hand
 *  socket. Takes the hand from any weapon (and vice versa); local + remotes. */
export function setTool(player: THREE.Group, spec: ItemSpec | null): void {
  equipTool(player, player.userData.parts as RigHandle, spec);
}

/** Rebuild an existing player rig's BODY in place — same group identity (game
 *  state, camera, colliders, labels all keep their reference), fresh geometry.
 *  Called whenever worn gear changes (local paperdoll or a remote's snapshot
 *  diff): swaps out the build's children, re-collects the rig joints, and
 *  re-seats the held weapon (its old mesh died with the old arm). Children that
 *  didn't come from the build (a remote's name label) are left untouched. */
export function redressPlayer(
  player: THREE.Group,
  look: number | Partial<Appearance>,
  gear: Gear,
  weapon: ItemSpec | null,
): void {
  // Dispose the old build's geometry before dropping it — the removed limb groups
  // (and any weapon/shield/tool seated under them) carry per-build geometry that
  // would otherwise leak on every gear change. disposeGroup keeps shared boxes +
  // cached toon materials, so re-dressing from the same factories stays safe.
  for (const c of (player.userData.buildKids as THREE.Object3D[] | undefined) ?? []) { player.remove(c); disposeGroup(c); }
  const fresh = buildPlayer(look, gear);
  const kids = [...fresh.children];
  for (const c of kids) player.add(c);
  player.userData.buildKids = kids;
  player.userData.parts = collectRig(player);
  // Cloak hem warp state was captured on `fresh` during attachCodedWear — move
  // it onto the live group (mesh refs are the same objects we reparented).
  player.userData.cloakHem = fresh.userData.cloakHem;
  invalidateGaitLimbs(player); // old limb groups are gone — force a re-collect or the walk freezes
  player.userData.weaponMesh = null; // gone with the old arm — never re-remove it
  setWeapon(player, weapon);
  // The shield died with the old arm too — re-seat it from the remembered spec
  // (equipOffhand stashes it on userData.shieldSpec, so no caller changes).
  player.userData.shieldMesh = null;
  setShield(player, (player.userData.shieldSpec as ItemSpec | null) ?? null);
  // Same for a held rod (plan 10) — but only when no weapon took the hand back
  // (setWeapon above cleared the tool if it seated one).
  if (!weapon) {
    player.userData.toolMesh = null;
    setTool(player, (player.userData.toolSpec as ItemSpec | null) ?? null);
  }
}

/** Length of the unarmed jab (seconds). */
export const PUNCH_DUR = UNARMED.attack.duration ?? 0.35;

/** Kick off an attack swing of the given selector — overrides the arm's gait pose
 *  for the clip's duration. Called from combat.ts on every player attack. */
export function startAttack(player: THREE.Group, sel: AttackSel): void {
  startRigAttack(player, sel);
}

/** Back-compat: an unarmed jab (the equipped-weapon-or-fists primary attack). */
export function startPunch(player: THREE.Group): void {
  startAttack(player, "primary");
}

/**
 * Drive the walk/run cycle — the shared humanoid gait with the player's profile,
 * then the shared weapon-pose pass (stance/swing), then the reading pose.
 * Call every frame.
 * @param running true while sprinting (Shift) — blends into a faster, bigger gait.
 * @returns vertical bob offset to add to the ground height.
 */
export function updatePlayer(player: THREE.Group, dt: number, moving: boolean, time: number, running = false): number {
  const rig = player.userData.parts as RigHandle;

  // The shared animator reads its inputs off userData (same flags the studio
  // and NPC drivers use); root:false — game.ts owns the player's transform,
  // we only return the bob for the ground-height resolve.
  player.userData.studioMoving = moving;
  player.userData.studioRunning = running;
  const bob = animateGait("humanoid", player, dt, time, { root: false });

  // Cloak hem follows hip swing so calves don't punch through a rigid shell.
  // After gait (hips written); no-ops when no coded cloak is equipped.
  updateCloakHem(player);

  // ---- Weapon pose overrides — run AFTER the gait so they win over it.
  // Carry sway: one-hand / bow free-arm + polearm coupled upper-body rock.
  const gs = gaitSnapshot(player);
  const swing = Math.sin(gs.phase);
  const armAmp = (PLAYER_PROFILE.armAmp + gs.runBlend * PLAYER_PROFILE.armAmpRun) * gs.walkBlend;
  applyWeaponPose(player, rig, dt, { swing, armAmp });

  // ---- Block pose (plan 14): shield raised across the chest while the block
  // is held — after the weapon pass so it wins over stances (local player,
  // remotes, and the studio all set userData.blocking the same way).
  if (player.userData.blocking) applyBlockPose(rig);

  // ---- Fishing pose (plan 10): while a line is out (fishing.ts sets
  // userData.fishing on cast/end) both hands grip the rod and hold it up toward
  // the water. A held-tool stance — the rod is parented to handMain but isn't a
  // weapon, so applyWeaponPose leaves it alone; this poses the arms over the top.
  if (player.userData.fishing) applyFishingPose(rig);

  // ---- Body pose (seated/sitting/prone/all-fours/kneeling): the blended
  // full-body override layer (src/anim/body-pose.ts) — after the weapon pass so
  // the pose's arms win over carry/ready, before reading so seated+journal
  // composes (legs stay seated, the book arms win). Root offset (y drop +
  // pitch/roll) is reported through userData.poseRoot and integrated by the
  // HOST (game.ts local player, net.ts remotes, stage.ts puppets) — never here,
  // same root:false contract as the gait.
  let poseRoot = player.userData.poseRoot as PoseRootOut | undefined;
  if (!poseRoot) poseRoot = player.userData.poseRoot = { dy: 0, pitch: 0, roll: 0 };
  applyBodyPose(player, rig, dt, poseRoot);

  // ---- Firestarting spark: kneel (body pose) owns legs/torso, but the spark
  // strike is an attack clip on the arms — re-apply weapon pose AFTER the kneel
  // so each fireSeq bump still reads as a fist/spark strike toward the pile.
  if (player.userData.fireStarting && (player.userData.attackT as number) > 0) {
    applyWeaponPose(player, rig, 0, { swing: 0, armAmp: 0 });
  }

  // ---- Reading pose (journal open) — runs LAST so it wins over gait + weapon poses.
  // Both hands come up to hold an open book at chest height; the head tips down to read
  // and the torso leans in a touch. Input is locked while reading, so this is static.
  if (player.userData.reading) {
    rig.shoulderL!.rotation.x = -0.95; rig.shoulderL!.rotation.z = 0.28; rig.elbowL!.rotation.x = -1.15;
    rig.shoulderR!.rotation.x = -0.95; rig.shoulderR!.rotation.z = -0.28; rig.elbowR!.rotation.x = -1.15;
    rig.torso!.rotation.x = 0.12;
    rig.head!.rotation.x = 0.35;
  }

  return bob;
}
